/**
 * Flexible markdown content parser for split view components
 * Handles various heading formats and section structures
 */

/**
 * Extract sections from markdown content with flexible pattern matching
 * @param {string} content - The markdown content to parse
 * @param {Array<Object>} sectionPatterns - Array of section patterns to match
 * @returns {Object} Parsed sections object
 */
export const extractSections = (content, sectionPatterns) => {
  const sections = {};
  
  // Initialize all sections as empty
  sectionPatterns.forEach(pattern => {
    sections[pattern.key] = pattern.isArray ? [] : '';
  });

  if (!content || content.length < 50) {
    console.log('Content too short for parsing:', content?.length || 0);
    return sections;
  }

  sectionPatterns.forEach(pattern => {
    const { key, keywords, isArray = false } = pattern;
    
    // Create flexible regex pattern that matches various heading formats
    const keywordPattern = keywords.join('|');
    
    // More flexible pattern matching:
    // - Supports ## 1. Header, ## Header, # Header formats
    // - Supports various keyword variations
    // - Handles content between sections better
    const regex = new RegExp(
      `#+\\s*(?:\\d+\\.?\\s*)?(?:${keywordPattern})[^\\n]*\\n+([\\s\\S]*?)(?=\\n#+\\s|\\n## |$)`,
      'i'
    );
    
    const match = content.match(regex);
    
    if (match && match[1]) {
      const sectionContent = match[1].trim();
      
      if (isArray) {
        // Parse list items for array sections
        sections[key] = parseListItems(sectionContent);
      } else {
        sections[key] = sectionContent;
      }
      
      console.log(`Extracted section "${key}":`, 
        isArray ? `${sections[key].length} items` : `${sectionContent.length} chars`
      );
    }
  });

  return sections;
};

/**
 * Parse list items from text content
 * @param {string} content - Content containing list items
 * @returns {Array} Array of parsed items
 */
export const parseListItems = (content) => {
  const items = [];

  // First, try to split by H3 headers (### Test Name)
  const h3Sections = content.split(/\n###\s+/);

  if (h3Sections.length > 1) {
    // Parse H3-based sections (common for test cases)
    for (let i = 1; i < h3Sections.length; i++) {
      const section = h3Sections[i];
      const lines = section.split('\n');
      let name = lines[0].trim();

      // Clean up the name - remove markdown links and backticks
      // Handle markdown link format: [Link Text](#anchor)
      if (name.includes('[') && name.includes('](')) {
        const linkMatch = name.match(/\[([^\]]+)\]/);
        if (linkMatch) {
          name = linkMatch[1]; // Extract just the link text
        }
      }

      // Handle formats like: `src/components/Button/Button.test.tsx`
      if (name.includes('`')) {
        // Extract filename from path
        const pathMatch = name.match(/`([^`]+)`/);
        if (pathMatch) {
          const fullPath = pathMatch[1];
          // Get just the filename without extension
          const filename = fullPath.split('/').pop().replace(/\.(test|spec)\.(ts|tsx|js|jsx)$/, '');
          // Convert to readable title: Button.test.tsx -> Button Test
          name = filename
            .split(/[._-]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        }
      }

      // Extract code blocks
      const codeBlockMatch = section.match(/```(?:javascript|js|typescript|ts)?\n([\s\S]*?)```/);
      const code = codeBlockMatch ? codeBlockMatch[1].trim() : '';

      // Remove code block from description
      let description = section.replace(/^.*?\n/, '').trim();
      if (codeBlockMatch) {
        description = description.replace(/```(?:javascript|js|typescript|ts)?\n[\s\S]*?```/, '').trim();
      }

      items.push({
        name: name,
        title: name,
        description: description,
        code: code
      });
    }

    return items;
  }

  // Pattern 1: Markdown items with bold names: * **Name**: Description
  const boldItems = Array.from(content.matchAll(/(?:[*-]|[0-9]+\.)\s+(?:\*\*|__)(.*?)(?:\*\*|__)(?:(?:\s*[::-]\s*)|:\s+|\s+-\s+|\n)([\s\S]*?)(?=\n(?:[*-]|[0-9]+\.)|\n###\s|$)/gs));

  for (const item of boldItems) {
    if (item[1] && item[2]) {
      const itemContent = item[2].trim();

      // Extract code block if present
      const codeBlockMatch = itemContent.match(/```(?:javascript|js|typescript|ts)?\n([\s\S]*?)```/);
      const code = codeBlockMatch ? codeBlockMatch[1].trim() : '';

      // Remove code block from description
      let description = itemContent;
      if (codeBlockMatch) {
        description = itemContent.replace(/```(?:javascript|js|typescript|ts)?\n[\s\S]*?```/, '').trim();
      }

      items.push({
        name: item[1].trim(),
        title: item[1].trim(),
        description: description,
        code: code
      });
    }
  }

  // Pattern 2: Simple list items with colon separator
  if (items.length === 0) {
    const colonItems = Array.from(content.matchAll(/(?:[*-]|[0-9]+\.)\s+(.*?):\s*([\s\S]*?)(?=\n(?:[*-]|[0-9]+\.)|\n###\s|$)/gs));

    for (const item of colonItems) {
      if (item[1] && item[2]) {
        const itemContent = item[2].trim();

        // Extract code block if present
        const codeBlockMatch = itemContent.match(/```(?:javascript|js|typescript|ts)?\n([\s\S]*?)```/);
        const code = codeBlockMatch ? codeBlockMatch[1].trim() : '';

        // Remove code block from description
        let description = itemContent;
        if (codeBlockMatch) {
          description = itemContent.replace(/```(?:javascript|js|typescript|ts)?\n[\s\S]*?```/, '').trim();
        }

        items.push({
          name: item[1].trim().replace(/\*\*/g, ''),
          title: item[1].trim().replace(/\*\*/g, ''),
          description: description,
          code: code
        });
      }
    }
  }

  // Pattern 3: Dash separated items
  if (items.length === 0) {
    const dashItems = Array.from(content.matchAll(/(?:[*-]|[0-9]+\.)\s+(.*?)\s*-\s*([\s\S]*?)(?=\n(?:[*-]|[0-9]+\.)|\n###\s|$)/gs));

    for (const item of dashItems) {
      if (item[1] && item[2]) {
        const itemContent = item[2].trim();

        // Extract code block if present
        const codeBlockMatch = itemContent.match(/```(?:javascript|js|typescript|ts)?\n([\s\S]*?)```/);
        const code = codeBlockMatch ? codeBlockMatch[1].trim() : '';

        // Remove code block from description
        let description = itemContent;
        if (codeBlockMatch) {
          description = itemContent.replace(/```(?:javascript|js|typescript|ts)?\n[\s\S]*?```/, '').trim();
        }

        items.push({
          name: item[1].trim().replace(/\*\*/g, ''),
          title: item[1].trim().replace(/\*\*/g, ''),
          description: description,
          code: code
        });
      }
    }
  }

  // Pattern 4: Just list items without descriptions
  if (items.length === 0) {
    const simpleItems = Array.from(content.matchAll(/(?:[*-]|[0-9]+\.)\s+(.*?)(?=\n[*-]|\n[0-9]+\.|\n\n|$)/g));

    for (const item of simpleItems) {
      const itemText = item[1].trim().replace(/\*\*/g, '');
      if (itemText.length > 0) {
        items.push({
          name: itemText,
          title: itemText,
          description: 'Details not specified',
          code: ''
        });
      }
    }
  }

  return items;
};

/**
 * Section patterns for different task types
 */
export const SECTION_PATTERNS = {
  'security-testing': [
    { key: 'overview', keywords: ['Security Testing Overview', 'Introduction', 'Overview', 'Objectives'] },
    { key: 'strategy', keywords: ['Security Testing Strategy', 'Testing Strategy', 'Approach', 'Methodology'] },
    { key: 'authenticationTesting', keywords: ['Authentication.*Testing', 'Authorization.*Testing', 'Auth.*Testing', 'Identity.*Testing'] },
    { key: 'inputValidation', keywords: ['Input Validation', 'Output Encoding', 'Injection.*Test', 'XSS.*Test', 'Validation.*Test'] },
    { key: 'dataProtection', keywords: ['Data Protection', 'Encryption', 'Privacy', 'Sensitive Data'] },
    { key: 'apiSecurity', keywords: ['API Security', 'API.*Test', 'Web.*Service', 'Endpoint.*Security'] },
    { key: 'infrastructureSecurity', keywords: ['Infrastructure Security', 'Configuration', 'Server.*Security', 'Dependency'] },
    { key: 'businessLogic', keywords: ['Business Logic', 'Logic.*Test', 'Workflow', 'Transaction.*Security'] }
  ],
  
  'dev-guidelines': [
    { key: 'summary', keywords: ['overview', 'summary', 'introduction', 'development guidelines'] },
    { key: 'codeOrganization', keywords: ['code organization', 'folder structure', 'file structure', 'code structure'] },
    { key: 'codingStandards', keywords: ['coding standards', 'best practices', 'code style', 'style guide'] },
    { key: 'designPatterns', keywords: ['design patterns', 'implementation', 'pattern'] },
    { key: 'solidPrinciples', keywords: ['solid', 'principles'] }
  ],
  
  'unit-testing': [
    // NOTE: Unit testing now uses H2/H3 structure parsed by parseUnitTestingSections()
    // H2 (##) = Category headers: "## Testing Strategy Overview", "## Component Tests"
    // H3 (###) = Individual test sections: "### Testing Approach", "### LoginComponent Tests"
    // This pattern definition is kept for reference only
    { key: 'strategy', keywords: ['Testing Strategy Overview', 'Strategy', 'Approach', 'Testing Methodology'] },
    { key: 'componentTests', keywords: ['Component.*Tests?', 'Module.*Tests?', 'Unit.*Tests?'], isArray: true },
    { key: 'apiTests', keywords: ['API.*Tests?', 'Service.*Tests?', 'Endpoint.*Tests?'], isArray: true },
    { key: 'stateTests', keywords: ['State.*Tests?', 'State Management', 'Reducer.*Tests?'], isArray: true },
    { key: 'integrationTests', keywords: ['Integration.*Tests?', 'Integration Points'], isArray: true },
    { key: 'utilityTests', keywords: ['Utility.*Tests?', 'Helper.*Tests?', 'Pure.*Function'], isArray: true },
    { key: 'testData', keywords: ['Test Data', 'Test.*Data.*Management', 'Fixtures', 'Test.*Setup'] },
    { key: 'coverage', keywords: ['Coverage', 'Test.*Coverage', 'Metrics'] }
  ],
  
  'functional-testing': [
    { key: 'strategy', keywords: ['Functional Testing Strategy', 'Testing Strategy', 'Approach'] },
    { key: 'userFlowCases', keywords: ['User Flow.*Test', 'End.*to.*End', 'User.*Journey'], isArray: true },
    { key: 'featureLevelCases', keywords: ['Feature.*Test', 'Feature.*Level'], isArray: true },
    { key: 'uiUxTests', keywords: ['UI.*UX.*Test', 'User.*Interface', 'UI.*Validation'], isArray: true },
    { key: 'businessLogicTests', keywords: ['Business Logic', 'Business.*Rule'], isArray: true },
    { key: 'integrationTests', keywords: ['Integration.*Test', 'System.*Integration'], isArray: true },
    { key: 'regressionTests', keywords: ['Regression.*Test', 'Regression.*Suite'], isArray: true }
  ],
  
  'performance-testing': [
    { key: 'strategy', keywords: ['Performance Testing Strategy', 'Testing Strategy', 'Approach'] },
    { key: 'loadTesting', keywords: ['Load.*Test', 'Load.*Scenario'], isArray: true },
    { key: 'stressTesting', keywords: ['Stress.*Test', 'Breaking.*Point'], isArray: true },
    { key: 'scalabilityTesting', keywords: ['Scalability.*Test', 'Scaling.*Test'], isArray: true },
    { key: 'enduranceTesting', keywords: ['Endurance.*Test', 'Long.*Running'], isArray: true },
    { key: 'frontendPerformance', keywords: ['Frontend.*Performance', 'Client.*Performance'], isArray: true },
    { key: 'backendPerformance', keywords: ['Backend.*Performance', 'Server.*Performance'], isArray: true }
  ],
  
  'tech-stack': [
    { key: 'summary', keywords: ['Executive Summary', 'Introduction', 'Overview', 'Summary'] },
    { key: 'frontend', keywords: ['Frontend', 'Frontend Technologies', 'Frontend Stack', 'UI Technologies', 'Client.*side'], isArray: true },
    { key: 'backend', keywords: ['Backend', 'Backend Technology', 'Server.*side', 'API.*Technology'], isArray: true },
    { key: 'database', keywords: ['Database', 'Database Solutions', 'Data.*Storage', 'Persistence'], isArray: true },
    { key: 'devops', keywords: ['DevOps', 'Infrastructure', 'Deployment', 'CI.*CD'], isArray: true },
    { key: 'thirdParty', keywords: ['Third.*party', 'External.*Services', 'APIs', 'Integrations'], isArray: true }
  ],
  
  'user-permissions': [
    { key: 'overview', keywords: ['Permission Architecture Overview', 'Overview', 'Introduction'] },
    { key: 'roles', keywords: ['Role.*Definition', 'User.*Roles', 'Roles'], isArray: true },
    { key: 'permissions', keywords: ['Resource.*Permission', 'Permissions', 'Access.*Control'], isArray: true },
    { key: 'matrix', keywords: ['Permission.*Matrix', 'Role.*Matrix', 'Access.*Matrix'], isArray: true },
    { key: 'implementation', keywords: ['Implementation', 'Technical.*Implementation'] },
    { key: 'bestPractices', keywords: ['Best.*Practices', 'Security.*Practices', 'Guidelines'] }
  ]
};

/**
 * Enhanced section extractor that uses predefined patterns
 * @param {string} content - Markdown content to parse
 * @param {string} taskType - Type of task (e.g., 'security-testing', 'dev-guidelines')
 * @returns {Object} Parsed sections
 */
export const extractSectionsByTaskType = (content, taskType) => {
  const patterns = SECTION_PATTERNS[taskType];
  
  if (!patterns) {
    console.warn(`No patterns defined for task type: ${taskType}`);
    return {};
  }
  
  return extractSections(content, patterns);
};